

















import { computed, defineComponent, getCurrentInstance, onMounted, onUnmounted } from "@vue/composition-api";
import { EVENT_NAMES } from "@scrile/api-provider/dist";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import useAdditionalSources from "@/hooks/useAdditionalSources";
import useAdminManagedUser from "@/hooks/useAdminManagedUser";
import useAuthUser from "@/hooks/useAuthUser";
import useDarkModeController from "@/hooks/useDarkModeController";
import useOnUserLogin from "@/hooks/useOnUserLogin";
import useSystemSettings from "@/hooks/useSystemSettings";
import useWatchMarkReadMessage from "@/hooks/useWatchMarkReadMessage";
import { ACTION_TYPES } from "@/store/const";
import providers from "@/lib/api";
import { IS_BROWSER, isNotificationsSupported } from "@/lib/browserUtils";
import emitter from "@/lib/emitter";
import { base64UrlToUint8Array } from "@/lib/encryptionHelper";
import { isGetUserMediaSupported, isWebRTCSupported } from "@/lib/webrtcHelpers";
import config from "@/config";
import MainNavigationView from "@/components/MainNavigationView";
import ModalPushNotificationsHelper from "@/components/ModalPushNotificationsHelper";
import NotificationBar from "@/components/NotificationBar";
import StatusNotificationBar from "@/components/StatusNotificationBar";

const LoginSignUpModal = () => import("@/components/LoginSignUpModal");
const BalanceManager = () => import("@/components/BalanceManager");
const PushManager = () => import("@/components/PushManager");
const PurchaseManager = () => import("@/components/PurchaseManager");
const CreatePost = () => import("@/components/CreatePost");
const ModalRefillBalance = () => import("@/components/ModalRefillBalance");
const CallController = () => import("@/components/CallController");
import "./styles/app.scss";
import "./styles/icons.scss";

if (IS_BROWSER) {
  document.title = config.siteName;
  import("@/lib/audioPlayer");
}

export default defineComponent({
  name: "App",
  components: {
    ModalPushNotificationsHelper,
    StatusNotificationBar,
    NotificationBar,
    MainNavigationView,
    LoginSignUpModal,
    BalanceManager,
    PushManager,
    PurchaseManager,
    CreatePost,
    ModalRefillBalance,
    CallController,
  },
  setup() {
    const { getData } = useSystemSettings();
    const { authUser } = useAuthUser();
    const vm = getCurrentInstance();
    useWatchMarkReadMessage();
    getData();
    const { init: initDarkMode } = useDarkModeController();
    const { init: loadSources } = useAdditionalSources();
    const { init: getAdminManagedStatus } = useAdminManagedUser();
    initDarkMode();
    loadSources();
    getAdminManagedStatus();

    const setUserHandler = (payload: { user: User | null; authorities: string[] | null } | null) => {
      if (payload === null || payload.user === null) {
        if (authUser.value !== null) vm?.$router.push("/logout");
      } else {
        vm?.$store.dispatch(ACTION_TYPES.LOGIN, payload);
      }
    };

    const featureCalling = computed(() => {
      return config.featureCalling && isWebRTCSupported() && isGetUserMediaSupported();
    });

    const initPushRequest = async () => {
      if (!isNotificationsSupported()) {
        return;
      }
      if (Notification.permission === "denied") {
        return;
      }
      if (Notification.permission === "default") {
        const requestedPermission = await Notification.requestPermission();
        if (requestedPermission !== "granted") {
          return;
        }
      }
      const reg = await navigator.serviceWorker.ready;
      const currentSubscription = await reg.pushManager.getSubscription();
      if (currentSubscription !== null) {
        return;
      }
      const unit8Buffer = base64UrlToUint8Array(config.webPushNotificationsPublicKey);
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: unit8Buffer,
      });
      const subscriptionsJson = subscription.toJSON();
      await providers.PushNotificationsProvider.webPushNotificationsSubscribe({
        data: {
          endpoint: subscriptionsJson.endpoint ?? "",
          p256dh: subscriptionsJson.keys?.p256dh ?? "",
          auth: subscriptionsJson.keys?.auth ?? "",
        },
      });
    };

    useOnUserLogin(() => {
      if (config.webPushNotificationsPublicKey && "serviceWorker" in navigator) {
        initPushRequest();
      }
    });
    emitter.on<any>(EVENT_NAMES.SET_USER, setUserHandler);
    onMounted(() => {
      if (config.webPushNotificationsPublicKey && "serviceWorker" in navigator && authUser.value) {
        window.addEventListener(
          "click",
          () => {
            initPushRequest();
          },
          { once: true }
        );
      }
    });
    onUnmounted(() => emitter.off<any>(EVENT_NAMES.SET_USER, setUserHandler));
    return {
      featureCalling,
      authUser,
    };
  },
});
